import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Link as LinkIcon } from "lucide-react";
import { client } from "@/lib/api";
import { CompactCard } from "@/pages/member/feedCard";

// WW-173 · chain cards from the v7 mockup: For You (A), been there? (D) and Discover (C).
const CSS = `
.ww-chain-box{background:#26264A;border:1px solid #2F2F52}
html[data-ww-theme="cream"] .ww-chain-box{background:#EFE6D6;border-color:#E4D7C0}
.ww-chain-pill{background:#FBF4E9;color:#1A1A30}
.ww-chain-pill svg{color:#7B3FC4}
html[data-ww-theme="cream"] .ww-chain-pill{background:#1A1A30;color:#FFFFFF}
html[data-ww-theme="cream"] .ww-chain-pill svg{color:#FFD60A}
`;
const PILL = { display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 999, padding: "3px 10px",
               fontFamily: "Arial", fontSize: 11, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", whiteSpace: "nowrap" };

export const shortDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", {
  timeZone: "Africa/Nairobi", weekday: "short", day: "numeric", month: "short" }).replace(",", "").toLowerCase() : "");

const venues = (n) => (n ? `${n} venue${n === 1 ? "" : "s"}` : "");

export function useChains() {
  const [chains, setChains] = useState({});
  useEffect(() => {
    client.get("/public/chains").then((r) => {
      const m = {};
      (r.data.chains || []).forEach((c) => { m[c.id] = c; });
      setChains(m);
    }).catch(() => setChains({}));
  }, []);
  return chains;
}

function ChainStyle() {
  return <style>{CSS}</style>;
}

function feedLine(c) {
  const when = c.status === "ended" ? `ended ${shortDay(c.last_end_at)}`
    : c.status === "on now" ? `on now until ${shortDay(c.last_end_at)}`
      : `${shortDay(c.first_start_at)} until ${shortDay(c.last_end_at)}`;
  return [when, venues(c.venue_count), c.from_price].filter(Boolean).join(" · ");
}

export function ChainFeedCard({ chain: c, onOpenPoster }) {
  const ended = c.status === "ended";
  const rows = ended ? c.events : c.events.filter((e) => !e.is_past);
  return (
    <div data-testid={`chain-card-${c.id}`} className="ww-chain-box"
         style={{ gridColumn: "1 / -1", borderRadius: 16, padding: "12px 0 14px", overflow: "hidden", minWidth: 0 }}>
      <ChainStyle />
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 14px" }}>
        <span className="ww-chain-pill" style={PILL} data-testid={`chain-card-pill-${c.id}`}>
          <LinkIcon size={12} strokeWidth={2.2} aria-hidden /> chain
        </span>
        {ended && (
          <span data-testid={`chain-card-ended-${c.id}`}
                style={{ border: "1.5px solid var(--ww-text-muted)", color: "var(--ww-text-muted)", fontFamily: "Arial", fontSize: 11,
                         fontWeight: 700, textTransform: "uppercase", borderRadius: 999, padding: "2px 9px" }}>ended</span>
        )}
        <Link to={`/app/chain/${c.id}`} data-testid={`chain-card-all-${c.id}`}
              style={{ marginLeft: "auto", fontFamily: "Poppins", fontWeight: 800, fontSize: 15, color: "var(--ww-text)", textDecoration: "none",
                       display: "inline-flex", alignItems: "center", minHeight: 32, margin: "-5px 0 -5px auto" }}>
          all {c.event_count} ›
        </Link>
      </div>
      <h3 data-testid={`chain-card-name-${c.id}`} className="lowercase"
          style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 21, letterSpacing: "-.02em", padding: "6px 14px 0", color: "var(--ww-text)", margin: 0 }}>
        {(c.name || "").toLowerCase()}
      </h3>
      <p data-testid={`chain-card-line-${c.id}`} style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)", padding: "2px 14px 10px", margin: 0 }}>
        {feedLine(c)}
      </p>
      <div data-testid={`chain-card-row-${c.id}`} className="flex overflow-x-auto"
           style={{ gap: 12, padding: "0 14px", scrollbarWidth: "none", scrollSnapType: "x mandatory", scrollPaddingLeft: 14 }}>
        {rows.map((e) => (
          <div key={e.id} style={{ flex: "0 0 auto", width: 290, maxWidth: "80vw", scrollSnapAlign: "start" }}>
            <CompactCard event={e} onOpenPoster={onOpenPoster} slim iWent={ended}
                         eyebrow={`event ${e.number} of ${c.event_count}`} />
          </div>
        ))}
      </div>
      <div aria-hidden style={{ height: 4, width: 150, background: "var(--ww-line)", borderRadius: 3, margin: "12px 14px 0" }} />
    </div>
  );
}

export function DiscoverChainCard({ chain: c }) {
  const [failed, setFailed] = useState(false);
  const status = c.status === "on now" ? "on now" : c.status === "ended" ? "ended" : `starts ${shortDay(c.first_start_at)}`;
  const line = [status, `${c.event_count} events`, venues(c.venue_count), `until ${shortDay(c.last_end_at)}`].filter(Boolean).join(" · ");
  const posters = c.events.filter((e) => e.cover_image_url);
  const shown = posters.slice(0, 6);
  return (
    <Link to={`/app/chain/${c.id}`} data-testid={`discover-chain-card-${c.id}`}
          style={{ flex: "0 0 auto", width: 280, background: "var(--ww-surface)", border: "1px solid var(--ww-line)",
                   borderRadius: 12, overflow: "hidden", textDecoration: "none", color: "var(--ww-text)", display: "block" }}>
      <div style={{ position: "relative", height: 118, background: "#0b0b1a" }}>
        {c.cover_image_url && !failed && (
          <img src={c.cover_image_url} alt="" onError={() => setFailed(true)}
               style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 44%" }} />
        )}
        <span style={{ ...PILL, position: "absolute", left: 10, top: 10, background: "rgba(26,26,48,.9)", color: "#FFFFFF" }}>
          <LinkIcon size={12} strokeWidth={2.2} aria-hidden style={{ color: "#FFD60A" }} /> chain · {c.event_count}
        </span>
      </div>
      <div style={{ padding: "10px 12px 12px" }}>
        <p className="lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 16, lineHeight: 1.15, letterSpacing: "-.025em", margin: 0 }}>
          {(c.name || "").toLowerCase()}
        </p>
        <p data-testid={`discover-chain-line-${c.id}`}
           style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", margin: "2px 0 8px" }}>
          {line}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {shown.map((e) => (
            <img key={e.id} src={e.cover_image_url} alt="" style={{ width: 26, height: 37, borderRadius: 3, objectFit: "cover", background: "var(--ww-sunken)" }} />
          ))}
          {c.event_count > shown.length && (
            <span style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)" }}>+{c.event_count - shown.length}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
